import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { selectCurrentProfileType, selectProfileLoading } from "newui/features/Auth/authSelectors";
import { useAppDispatch, useAppSelector } from "newui/store/hooks";
import {
	createContext,
	useMemo,
	useCallback,
	useState,
	ReactNode,
	useContext,
	useEffect,
} from "react";
import { useSelector } from "react-redux";
import { isProcurerNBidder } from "utils/helpers";
import { setCurrentProfileType, setIsProfileLoading } from "newui/features/Auth/authSlice";
import { CompanyProfileType } from "newui/features/Auth/types/CompanyProfileType";
import { navigate } from "@reach/router";
import { useNavigationPaths } from "../hooks/useNavigationPaths";

export enum CurrentAccountType {
	BIDDER = "Bidder",
	PROCURER = "Procurer"
}

// Define the shape of the context
interface ProfileContextValue {
	currentProfile: CurrentAccountType;
	forceRestartTour: boolean;
	isAccountSwitchable: boolean;
	isClientProfileActive: boolean;
	isRfqFeatureEnabled: boolean;
	setForceRestartTour: React.Dispatch<React.SetStateAction<boolean>>;
	userId?: string | number;
	changeProfileType: (profileType: CompanyProfileType) => void;
	resetTour: () => Promise<void>;
}

// Create context with default value
export const ProfileContext = createContext<ProfileContextValue>({
	isClientProfileActive: true,
	currentProfile: CurrentAccountType.PROCURER,
	isAccountSwitchable: false,
	changeProfileType: () => {
		// default do nothing
	},
	isRfqFeatureEnabled: false,
	userId: undefined,
	forceRestartTour: false,
	setForceRestartTour: () => {
		// default do nothing
	},
	resetTour: async () => {
		// default do nothing
	},
});

// Helper function typing
const getUserAccountType = (profileType: CompanyProfileType) => {
	return {
		currentAccountType: profileType === "client" ? CurrentAccountType.PROCURER : CurrentAccountType.BIDDER,
		isClientProfileActive: profileType === "client",
	};
};

// Props typing
interface ProfileContextProviderProps {
	children: ReactNode;
}

export const ProfileContextProvider = ({ children }: ProfileContextProviderProps) => {
	const role = useAppSelector(selectCurrentProfileType);
	const isProfileLoading = useAppSelector(selectProfileLoading);
	const { getHomePageForProfileType } = useNavigationPaths();
	const dispatch = useAppDispatch();

	const { currentUser } = useCurrentUser();
	const company = useCurrentCompany();

	const userId = currentUser?.id;

	const { currentAccountType, isClientProfileActive } = getUserAccountType(role);

	useEffect(() => {
		if (role && currentUser && currentAccountType && isProfileLoading) {
			dispatch(setIsProfileLoading(false));
		}
	}, [currentUser, currentAccountType, dispatch, isProfileLoading, role]);

	const rfqTotal = useSelector((state: any) => state?.supplier_rfq?.total ?? 0);

	const isRfqFeatureEnabled =
		role === "client" ? company?.enable_rfx_functionality ?? false : true;

	const isAccountSwitchable = isProcurerNBidder(company);

	const changeProfileType = useCallback(
		(profileType: CompanyProfileType) => {
			// safely navigate to 'home' page for respective profile
			dispatch(setIsProfileLoading(true));
			navigate(getHomePageForProfileType(profileType), { replace: true });
			dispatch(setCurrentProfileType(profileType));
		},
		[dispatch, getHomePageForProfileType]
	);

	const [forceRestartTour, setForceRestartTour] = useState(false);

	const resetTour = useCallback(async () => {
		setForceRestartTour(true);
	}, []);

	const contextValue = useMemo(
		() => ({
			isClientProfileActive,
			currentProfile: currentAccountType,
			isAccountSwitchable,
			changeProfileType,
			isRfqFeatureEnabled,
			userId,
			forceRestartTour,
			setForceRestartTour,
			resetTour,
		}),
		[
			changeProfileType,
			currentAccountType,
			isClientProfileActive,
			isAccountSwitchable,
			isRfqFeatureEnabled,
			userId,
			forceRestartTour,
			setForceRestartTour,
			resetTour,
		]
	);

	return <ProfileContext.Provider value={contextValue}>{children}</ProfileContext.Provider>;
};

export const useProfileContext = (): ProfileContextValue => {
	const context = useContext(ProfileContext);
	if (!context) {
		throw new Error("useProfileContext must be used within ProfileContextProvider");
	}
	return context;
};